/**
 * Who made a payment, and how — in plain words, from what the ledger holds.
 *
 * A payment is an `invoices` row with `metadata.type = 'credit'`. What was
 * recorded about the payer has changed over time and differs by path, so this
 * reads every signal that exists and says "not recorded" when none does. It
 * never guesses: an older row with no payer shows a neutral label.
 *
 * Signals, in the order they are trusted:
 *
 *   autopay   `method` autopay / autopay_ach, `metadata.charged_via` starting
 *             "Stripe Autopay", `metadata.purpose` "Automatic balance payment",
 *             or `metadata.scheduled_payment_id` (the scheduled-charge job).
 *   office    `processed_by.role` is a staff role; or the method can only
 *             happen at the desk (cash, check, card reader, account credit,
 *             point of sale); or a registration flagged `registered_by_admin`.
 *   online    `processed_by.role` is a member role; or `metadata.purpose` is
 *             "Member self-pay invoice".
 *   unknown   none of the above.
 *
 * Nothing here returns a card number, bank detail or processor id: the method
 * is a word ("Card", "Bank account", "Cash"), never an identifier.
 */

export type PaymentChannel = "autopay" | "office" | "online" | "unknown";

export interface PaymentOrigin {
  channel: PaymentChannel;
  /** One plain sentence: "Paid online by Pat Example". */
  summary: string;
  /** The parent who paid online, when recorded. */
  payerName: string | null;
  /** The staff member who took the payment. Only returned for staff viewers. */
  staffName: string | null;
  /** "Card", "Bank account", "Cash", "Check", "Card reader", ... or null. */
  methodLabel: string | null;
}

export const ORIGIN_NOT_RECORDED = "How this payment was made was not recorded";
/** The value shown in a labelled field (Paid by, Taken by, Payment method) when it is unknown. */
export const FIELD_NOT_RECORDED = "Not recorded";
/** @deprecated a field's label already says what is missing; use FIELD_NOT_RECORDED. */
export const PAYER_NOT_RECORDED = FIELD_NOT_RECORDED;
export const METHOD_NOT_RECORDED = FIELD_NOT_RECORDED;

/** "Card payment. Whether it was made online or in the office was not recorded." */
export function channelNotRecorded(methodLabel: string): string {
  return `${methodLabel} payment. Whether it was made online or in the office was not recorded.`;
}

interface Options {
  /** Staff see which staff member took an office payment; families do not. */
  viewerIsStaff?: boolean;
}

const MEMBER_ROLES = new Set(["member", "customer", "parent", "primary", "secondary", "guardian"]);

const lower = (value: unknown): string => String(value ?? "").trim().toLowerCase();

/** `processed_by` is a json column: an object, a JSON string, or a bare word. */
function readProcessedBy(raw: unknown): { name: string | null; role: string; word: string } {
  let value: any = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{")) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        value = trimmed;
      }
    }
  }
  if (value && typeof value === "object") {
    return { name: cleanName(value.name), role: lower(value.role), word: "" };
  }
  // A bare string is a system marker ("cron") or a user id — never a name.
  return { name: null, role: "", word: lower(value) };
}

/**
 * The payment forms build the name as `first + " " + last`, so a missing part
 * was stored as the literal text "undefined". Drop those rather than show them.
 */
function cleanName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const name = raw
    .split(/\s+/)
    .filter((part) => part && part !== "undefined" && part !== "null")
    .join(" ")
    .trim();
  return name || null;
}

function methodLabelOf(invoice: any): string | null {
  const md = invoice?.metadata || {};
  const candidates = [md.display_payment_method, md.charged_via, invoice?.method, md.payment_type, md.source];
  for (const candidate of candidates) {
    const label = METHOD_LABELS[lower(candidate)];
    if (label) return label;
  }
  return null;
}

const METHOD_LABELS: Record<string, string> = {
  card: "Card",
  "card (reader)": "Card reader",
  card_reader: "Card reader",
  cash: "Cash",
  check: "Check",
  cheque: "Check",
  "bank account": "Bank account",
  "bank account (ach)": "Bank account",
  bank_account: "Bank account",
  us_bank_account: "Bank account",
  ach: "Bank account",
  autopay: "Card",
  autopay_ach: "Bank account",
  "stripe autopay": "Card",
  "stripe autopay (ach)": "Bank account",
  account_credit: "Account credit",
  balance: "Account credit",
  refund: "Refund",
  // "Stripe" as charged_via means an online card charge.
  stripe: "Card",
};

/** Methods that can only be taken by staff at the desk. */
const OFFICE_ONLY_METHODS = new Set(["Cash", "Check", "Card reader", "Account credit"]);

function isAutopay(invoice: any, processedWord: string): boolean {
  const md = invoice?.metadata || {};
  const method = lower(invoice?.method);
  return (
    method === "autopay" ||
    method === "autopay_ach" ||
    lower(md.charged_via).startsWith("stripe autopay") ||
    lower(md.purpose) === "automatic balance payment" ||
    Boolean(md.scheduled_payment_id) ||
    processedWord === "cron" ||
    processedWord === "cron_job"
  );
}

/** "Feb 1, 2026" from a payment's date, read as a calendar day. */
function formatPaymentDay(invoice: any): string | null {
  // `due_date` is the club's calendar day (naive timestamp); prefer it so the
  // day shown here is the day the row sits on in the table.
  const raw = invoice?.due_date || invoice?.payment_date || invoice?.created_at;
  const match = typeof raw === "string" ? raw.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * An account credit staff added (goodwill, a correction): money the club
 * gave, not money it took. It is not a payment, has no receipt and cannot be
 * refunded to a card. Until 2026-10-10 its pop-up said "Payment taken in the
 * office" and offered Issue Refund.
 */
export function isAccountCreditEntry(invoice: any): boolean {
  return methodLabelOf(invoice) === "Account credit";
}

export function describePaymentOrigin(invoice: any, options: Options = {}): PaymentOrigin {
  const { viewerIsStaff = false } = options;
  const md = invoice?.metadata || {};
  const processed = readProcessedBy(invoice?.processed_by);
  const methodLabel = methodLabelOf(invoice);

  if (isAutopay(invoice, processed.word)) {
    const day = formatPaymentDay(invoice);
    return {
      channel: "autopay",
      summary: day ? `Autopay by the system on ${day}` : "Autopay by the system",
      payerName: null,
      staffName: null,
      methodLabel,
    };
  }

  const accountCredit = methodLabel === "Account credit";
  const office = (staffName: string | null): PaymentOrigin => ({
    channel: "office",
    summary: accountCredit
      ? viewerIsStaff && staffName
        ? `Account credit added by ${staffName}`
        : "Account credit added by the club"
      : viewerIsStaff && staffName
        ? `Payment taken in the office by ${staffName}`
        : "Payment taken in the office",
    payerName: null,
    staffName: viewerIsStaff ? staffName : null,
    methodLabel,
  });

  const online = (payerName: string | null): PaymentOrigin => ({
    channel: "online",
    summary: payerName ? `Paid online by ${payerName}` : "Paid online",
    payerName,
    staffName: null,
    methodLabel,
  });

  if (processed.role) {
    return MEMBER_ROLES.has(processed.role) ? online(processed.name) : office(processed.name);
  }

  if (lower(md.purpose) === "member self-pay invoice") return online(null);
  if (md.registered_by_admin === true) return office(null);
  if (lower(md.purpose).startsWith("pos sale")) return office(null);
  if (methodLabel && OFFICE_ONLY_METHODS.has(methodLabel)) return office(null);

  return {
    channel: "unknown",
    // Only say the method is unknown when it is: the Payment method field
    // sits right under this line.
    summary: methodLabel ? channelNotRecorded(methodLabel) : ORIGIN_NOT_RECORDED,
    payerName: null,
    staffName: null,
    methodLabel,
  };
}

export type PaymentOriginField = "paidBy" | "takenBy" | "addedBy" | "paymentMethod";

/**
 * The labelled lines shown under the summary, in order. Built here, beside the
 * summary, so the two cannot disagree: a field that is unknown reads
 * "Not recorded", and the summary never calls something unknown that a field
 * below it names.
 *
 * `sharedPaidBy` is the account member a shared-account payment was recorded
 * against (from the tooltip), used when the payment row itself has no payer.
 */
export function paymentOriginFields(
  origin: PaymentOrigin,
  options: { viewerIsStaff?: boolean; sharedPaidBy?: string | null } = {}
): { field: PaymentOriginField; value: string }[] {
  const { viewerIsStaff = false, sharedPaidBy = null } = options;
  const out: { field: PaymentOriginField; value: string }[] = [];
  if (origin.channel === "online") {
    out.push({ field: "paidBy", value: origin.payerName || sharedPaidBy || FIELD_NOT_RECORDED });
  } else if (sharedPaidBy) {
    out.push({ field: "paidBy", value: sharedPaidBy });
  }
  // An account credit is money the club gave, not a payment someone made:
  // staff "added" it, and there is no payment method to name (2026-10-10).
  const accountCredit = origin.methodLabel === "Account credit";
  if (origin.channel === "office" && viewerIsStaff) {
    out.push({ field: accountCredit ? "addedBy" : "takenBy", value: origin.staffName || FIELD_NOT_RECORDED });
  }
  if (!accountCredit) {
    out.push({ field: "paymentMethod", value: origin.methodLabel || FIELD_NOT_RECORDED });
  }
  return out;
}
